import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer,
} from 'recharts';
import { useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import {
  Shield, User, Newspaper, Scale, AlertTriangle, Building,
  BadgeCheck, ChevronDown, ChevronRight, Loader2,
  BookmarkPlus, FileSearch, Zap, Network, Database,
  GitBranch, Phone, Mail, Fingerprint, Globe, Search,
  AlertOctagon, CheckCircle2, ArrowRight,
  TrendingUp, Hash, X, RotateCcw,
} from 'lucide-react';
import { NetworkGraph } from '@/components/profiler/DossierSection/NetworkGraph';
import { PivotChain } from '@/components/profiler/PivotChain';
import { useAuth } from '@/contexts/AuthContext';
import { useCDDCase } from '@/hooks/profiler/useCDDCase';
import { profilerApi } from '@/api/profiler';
import { RiskTierBadge } from '@/components/profiler/RiskTierBadge';
import { ConfidenceBar } from '@/components/profiler/ConfidenceBar';
import { ReportDownloadBar } from '@/components/profiler/ReportDownloadBar';
import { TIER_COLOR, MODULE_LABELS, SEED_LABELS } from '@/types/profiler';
import type { CheckResult, SanctionsHit, SeedType, IdentifierVariant, IdentifierCluster } from '@/types/profiler';

// ── Constants ─────────────────────────────────────────────────────────────────

const MODULE_ICONS: Record<string, LucideIcon> = {
  SANCTIONS: Shield, PEP: User, ADVERSE_MEDIA: Newspaper, ENFORCEMENT: Scale,
  ATO: AlertTriangle, INDIA_CORPORATE: Building, INDIA_IDENTITY: BadgeCheck,
  WEALTH: Zap, SOCIAL: Globe, COURT: Scale, BREACH_ENRICHMENT: Database,
};

const TYPE_COLORS: Record<string, string> = {
  NAME: '#00C8FF', EMAIL: '#2979FF', PHONE: '#00C853', ADDRESS: '#F0B429',
  COMPANY: '#F0B429', USERNAME: '#A855F7', PASSPORT: '#FF6B1A',
  PAN: '#00C853', DL: '#00C853', VOTER_ID: '#00C853', DOB: '#FF6B1A',
  GSTIN: '#00C853', AADHAAR: '#00C853', RELATION: '#00C8FF', DOMAIN: '#5A7A9A',
};

const TYPE_ICONS: Partial<Record<string, LucideIcon>> = {
  EMAIL: Mail, PHONE: Phone, NAME: User, USERNAME: Hash,
  PASSPORT: Fingerprint, DOMAIN: Globe, COMPANY: Building, ADDRESS: Globe,
};

// ── Utilities ─────────────────────────────────────────────────────────────────

function fmt(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function initials(name?: string) {
  if (!name) return '?';
  return name.split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('');
}

function grokTag(source: string): string | null {
  const m = source.match(/\(grok:([^)]+)\)/);
  return m ? m[1] : null;
}

function normScore(v: number) {
  return Math.round(v > 1 ? v : v * 100);
}

// ── SVG Arc Gauge ─────────────────────────────────────────────────────────────

function RiskArcGauge({ score, tier, tierColor }: { score: number; tier: string; tierColor: string }) {
  const r = 68;
  const cx = 90;
  const cy = 96;
  const startDeg = -215;
  const totalDeg = 250;
  const scoreDeg = startDeg + (score / 100) * totalDeg;

  const toRad = (d: number) => (d * Math.PI) / 180;
  const arc = (a: number, b: number) => {
    const x1 = cx + r * Math.cos(toRad(a));
    const y1 = cy + r * Math.sin(toRad(a));
    const x2 = cx + r * Math.cos(toRad(b));
    const y2 = cy + r * Math.sin(toRad(b));
    const large = Math.abs(b - a) > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };

  // Tick marks
  const ticks = [0, 25, 50, 75, 100].map(pct => {
    const deg = startDeg + (pct / 100) * totalDeg;
    const ri = r + 10;
    const ro = r + 16;
    return {
      x1: cx + ri * Math.cos(toRad(deg)),
      y1: cy + ri * Math.sin(toRad(deg)),
      x2: cx + ro * Math.cos(toRad(deg)),
      y2: cy + ro * Math.sin(toRad(deg)),
      lx: cx + (ro + 8) * Math.cos(toRad(deg)),
      ly: cy + (ro + 8) * Math.sin(toRad(deg)),
      label: pct,
    };
  });

  const glowId = `glow-${tier}`;

  return (
    <svg width="180" height="170" viewBox="0 0 180 170">
      <defs>
        <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {/* Outer ring */}
      <circle cx={cx} cy={cy} r={r + 20} fill="none" stroke="#0F1928" strokeWidth="1" />
      {/* Background track */}
      <path d={arc(startDeg, startDeg + totalDeg)} fill="none" stroke="#0F1928" strokeWidth="10" strokeLinecap="round" />
      {/* Score fill */}
      <motion.path
        d={arc(startDeg, scoreDeg)}
        fill="none"
        stroke={tierColor}
        strokeWidth="10"
        strokeLinecap="round"
        filter={`url(#${glowId})`}
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
      />
      {/* Tick marks */}
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} stroke="#2A3A50" strokeWidth="1.5" strokeLinecap="round" />
          {t.label % 50 === 0 && (
            <text x={t.lx} y={t.ly + 3} textAnchor="middle" fill="#3A5A7A" fontSize="7" fontFamily="monospace">
              {t.label}
            </text>
          )}
        </g>
      ))}
      {/* Score */}
      <text x={cx} y={cy - 14} textAnchor="middle" fill={tierColor} fontSize="36" fontWeight="900" fontFamily="monospace" filter={`url(#${glowId})`}>
        {Math.round(score)}
      </text>
      <text x={cx} y={cy + 8} textAnchor="middle" fill="#3A5A7A" fontSize="8" fontFamily="monospace" letterSpacing="2">
        RISK SCORE
      </text>
      <text x={cx} y={cy + 24} textAnchor="middle" fill={tierColor} fontSize="11" fontFamily="monospace" fontWeight="800" letterSpacing="3">
        {tier}
      </text>
    </svg>
  );
}

// ── Module Status Grid ────────────────────────────────────────────────────────

function ModuleStatusGrid({ results }: { results: CheckResult[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (!results.length) return null;

  return (
    <div className="flex flex-col gap-2 h-full">
      <p style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.18em' }} className="mb-1">
        MODULE CHECKS
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {results.slice(0, 8).map((r, i) => {
          const Icon = MODULE_ICONS[r.module] ?? Shield;
          const hit = r.hit_found ?? false;
          const status = r.status ?? 'COMPLETE';
          const color = hit ? '#FF1F1F' : status === 'DEGRADED' ? '#F0B429' : '#00C853';
          const StatusIcon = hit ? AlertOctagon : status === 'DEGRADED' ? AlertTriangle : CheckCircle2;
          const conf = r.confidence ?? 0;
          const isOpen = expanded === r.module;

          return (
            <div key={i}>
              <button
                onClick={() => setExpanded(isOpen ? null : r.module)}
                style={{
                  background: hit ? '#FF1F1F08' : '#0C1118',
                  border: `1px solid ${hit ? '#FF1F1F25' : '#182030'}`,
                  borderLeft: `2px solid ${color}`,
                  borderRadius: 8,
                  padding: '10px 12px',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <Icon size={11} style={{ color, flexShrink: 0 }} />
                  <StatusIcon size={11} style={{ color }} />
                </div>
                <div style={{ color: '#B0C8E0', fontFamily: 'monospace', fontSize: 9, fontWeight: 700, marginBottom: 4 }}>
                  {(MODULE_LABELS[r.module] ?? r.module).replace('India ', '').toUpperCase()}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color, fontFamily: 'monospace', fontSize: 7, fontWeight: 700, letterSpacing: '0.08em' }}>
                    {hit ? 'HIT' : status === 'DEGRADED' ? 'DEGRADED' : 'CLEAR'}
                  </span>
                  {conf > 0 && (
                    <span style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 7 }}>
                      {Math.round(conf * 100)}%
                    </span>
                  )}
                </div>
                {conf > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <ConfidenceBar value={conf} color={color} height={2} />
                  </div>
                )}
              </button>
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ overflow: 'hidden', background: '#090D13', border: '1px solid #182030', borderTop: 'none', borderRadius: '0 0 8px 8px' }}
                  >
                    <pre style={{ fontFamily: 'monospace', fontSize: 8, color: '#3A5A7A', padding: '8px 10px', overflowX: 'auto', maxHeight: 160 }}>
                      {JSON.stringify(r.detail ?? {}, null, 2)}
                    </pre>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Breach Enrichment Trail ───────────────────────────────────────────────────

function BreachEnrichmentTrail({ variants, ato, cluster, discardedSet, onDiscard }: {
  variants: IdentifierVariant[];
  ato?: { breach_count?: number; stealer_log_count?: number; breach_sources?: string[] };
  cluster?: IdentifierCluster;
  discardedSet: Set<string>;
  onDiscard: (value: string, undo: boolean) => void;
}) {
  const r1 = variants.filter(v => v.source.startsWith('breach(') && !v.source.includes('pivot'));
  const pivotValidated = variants.filter(v => v.source.includes('breach_pivot(validated)'));
  const pivotUnverified = variants.filter(v => v.source.includes('breach_pivot(unverified)'));
  const biDirConfirmed = variants.filter(v => v.source === 'breach_validated' || v.source.startsWith('breach_validated'));
  const grokAccepted = variants.filter(v => v.source.includes('grok:ok'));
  const grokRejected = variants.filter(v => v.source.match(/grok:(rejected|associated|possible)/));

  const dbCount = ato?.breach_sources?.length ?? (r1.length > 0 ? '29+' : 0);
  const stealerCount = ato?.stealer_log_count ?? 0;

  const steps = [
    {
      label: 'SEED QUERY',
      icon: Search,
      color: '#2979FF',
      stat: `${typeof dbCount === 'number' ? dbCount : dbCount} DBs`,
      sub: 'LeakOSINT R1',
    },
    {
      label: 'PII EXTRACTED',
      icon: Database,
      color: '#00C8FF',
      stat: `${r1.length} identifiers`,
      sub: 'Phones · Emails · Names',
    },
    {
      label: 'PIVOT QUERIES',
      icon: GitBranch,
      color: '#A855F7',
      stat: `${pivotValidated.length + pivotUnverified.length} results`,
      sub: `${biDirConfirmed.length} bidirectional ✓`,
    },
    {
      label: 'GROK VERIFY',
      icon: Shield,
      color: '#F0B429',
      stat: `${grokAccepted.length} accepted`,
      sub: `${grokRejected.length} flagged as leads`,
    },
    {
      label: 'CLUSTER BUILT',
      icon: Network,
      color: '#00C853',
      stat: `${variants.length} total`,
      sub: stealerCount > 0 ? `${stealerCount} stealer log(s)` : 'No stealer logs',
    },
  ];

  return (
    <div>
      <p style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.18em', marginBottom: 16 }}>
        ENRICHMENT PIPELINE
      </p>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
        {steps.map((step, i) => {
          const Icon = step.icon;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.3 }}
                style={{
                  background: `${step.color}08`,
                  border: `1px solid ${step.color}25`,
                  borderRadius: 10,
                  padding: '14px 12px',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%',
                    background: `${step.color}18`,
                    border: `1px solid ${step.color}40`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Icon size={11} style={{ color: step.color }} />
                  </div>
                  <span style={{ color: step.color, fontFamily: 'monospace', fontSize: 7, fontWeight: 700, letterSpacing: '0.1em', lineHeight: 1.2 }}>
                    {step.label}
                  </span>
                </div>
                <div style={{ color: step.color, fontFamily: 'monospace', fontSize: 14, fontWeight: 900, letterSpacing: '-0.02em' }}>
                  {step.stat}
                </div>
                <div style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8, marginTop: 2 }}>
                  {step.sub}
                </div>
              </motion.div>
              {i < steps.length - 1 && (
                <ArrowRight size={12} style={{ color: '#1E2E40', flexShrink: 0, margin: '0 4px' }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Pivot breakdown */}
      {(pivotValidated.length + pivotUnverified.length) > 0 && (
        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div style={{ background: '#00C85308', border: '1px solid #00C85320', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ color: '#00C853', fontFamily: 'monospace', fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', marginBottom: 6 }}>
              PIVOT-VALIDATED IDENTIFIERS
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {pivotValidated.map((v, i) => {
                const c = TYPE_COLORS[v.type] ?? '#5A7A9A';
                const isDiscarded = discardedSet.has(v.value);
                return (
                  <span
                    key={i}
                    className="group"
                    style={{
                      background: isDiscarded ? '#0F1928' : `${c}12`,
                      color: isDiscarded ? '#3A5A7A' : c,
                      fontFamily: 'monospace', fontSize: 8,
                      padding: '2px 4px 2px 7px', borderRadius: 99,
                      border: `1px solid ${isDiscarded ? '#182030' : c + '25'}`,
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      textDecoration: isDiscarded ? 'line-through' : undefined,
                    }}
                  >
                    {v.value}
                    <button
                      onClick={() => onDiscard(v.value, isDiscarded)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: isDiscarded ? '#00C853' : '#FF4444', display: 'flex' }}
                    >
                      {isDiscarded ? <RotateCcw size={7} /> : <X size={7} />}
                    </button>
                  </span>
                );
              })}
              {pivotValidated.length === 0 && (
                <span style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8 }}>None</span>
              )}
            </div>
          </div>
          <div style={{ background: '#A855F708', border: '1px solid #A855F720', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ color: '#A855F7', fontFamily: 'monospace', fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', marginBottom: 6 }}>
              BIDIRECTIONALLY CONFIRMED
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {biDirConfirmed.map((v, i) => {
                const isDiscarded = discardedSet.has(v.value);
                return (
                  <span
                    key={i}
                    style={{
                      background: isDiscarded ? '#0F1928' : '#A855F712',
                      color: isDiscarded ? '#3A5A7A' : '#A855F7',
                      fontFamily: 'monospace', fontSize: 8,
                      padding: '2px 4px 2px 7px', borderRadius: 99,
                      border: `1px solid ${isDiscarded ? '#182030' : '#A855F730'}`,
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      textDecoration: isDiscarded ? 'line-through' : undefined,
                    }}
                  >
                    {v.type}: {v.value}
                    <button
                      onClick={() => onDiscard(v.value, isDiscarded)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: isDiscarded ? '#00C853' : '#FF4444', display: 'flex' }}
                    >
                      {isDiscarded ? <RotateCcw size={7} /> : <X size={7} />}
                    </button>
                  </span>
                );
              })}
              {biDirConfirmed.length === 0 && (
                <span style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8 }}>None</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pivot chain visualization */}
      {cluster && (
        <div style={{ marginTop: 14 }}>
          <PivotChain cluster={cluster} />
        </div>
      )}
    </div>
  );
}

// ── Investigative Leads ───────────────────────────────────────────────────────

function InvestigativeLeads({ variants, onInvestigate }: {
  variants: IdentifierVariant[];
  onInvestigate: (value: string, type: string) => void;
}) {
  const associated = variants.filter(v => v.source.includes('grok:associated_person'));
  const aliases = variants.filter(v => v.source.includes('grok:possible_alias'));
  const assocPhones = variants.filter(v => v.source.includes('grok:associated_phone'));
  const assocCompanies = variants.filter(v => v.source.includes('grok:associated_company'));

  const total = associated.length + aliases.length + assocPhones.length + assocCompanies.length;
  if (total === 0) return null;

  const Section = ({ title, items, color, seedType }: {
    title: string; items: IdentifierVariant[]; color: string; seedType: string;
  }) => {
    if (!items.length) return null;
    return (
      <div>
        <p style={{ color, fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', marginBottom: 8 }}>{title}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((v, i) => (
            <div key={i} style={{
              background: `${color}06`,
              border: `1px solid ${color}20`,
              borderRadius: 8,
              padding: '9px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0, boxShadow: `0 0 6px ${color}80` }} />
                <div>
                  <div style={{ color, fontFamily: 'monospace', fontSize: 10, fontWeight: 700, wordBreak: 'break-all' }}>{v.value}</div>
                  <div style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 7, marginTop: 1 }}>
                    conf {Math.round(v.confidence * 100)}% · {v.source.split('(')[0]}
                  </div>
                </div>
              </div>
              <button
                onClick={() => onInvestigate(v.value, seedType)}
                style={{
                  background: `${color}12`,
                  border: `1px solid ${color}30`,
                  color,
                  fontFamily: 'monospace',
                  fontSize: 7,
                  fontWeight: 700,
                  padding: '3px 8px',
                  borderRadius: 99,
                  cursor: 'pointer',
                  flexShrink: 0,
                  letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                }}
              >
                INVESTIGATE
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12, padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <p style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>
            Investigative Leads
          </p>
          <p style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8, marginTop: 2 }}>
            Identifiers linked to the subject — not confirmed as belonging to them
          </p>
        </div>
        <span style={{ background: '#F0B42918', color: '#F0B429', fontFamily: 'monospace', fontSize: 8, padding: '3px 10px', borderRadius: 99, border: '1px solid #F0B42930' }}>
          {total} LEADS
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Section title="ASSOCIATED PERSONS" items={associated} color="#00C8FF" seedType="NAME" />
        <Section title="POSSIBLE ALIASES" items={aliases} color="#A855F7" seedType="USERNAME" />
        <Section title="ASSOCIATED PHONES" items={assocPhones} color="#00C853" seedType="PHONE" />
        <Section title="ASSOCIATED COMPANIES" items={assocCompanies} color="#F0B429" seedType="COMPANY" />
      </div>
    </div>
  );
}

// ── Sanctions Panel ───────────────────────────────────────────────────────────

function SanctionsPanel({ hits }: { hits: SanctionsHit[] }) {
  if (!hits.length) return null;
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div style={{ background: '#090D13', border: '1px solid #FF1F1F25', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ background: '#FF1F1F10', borderBottom: '1px solid #FF1F1F25', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertOctagon size={14} style={{ color: '#FF1F1F' }} />
          <span style={{ color: '#FF1F1F', fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>SANCTIONS MATCHES</span>
        </div>
        <span style={{ color: '#FF1F1F', fontFamily: 'monospace', fontSize: 8, background: '#FF1F1F18', padding: '3px 10px', borderRadius: 99, border: '1px solid #FF1F1F30' }}>
          {hits.length} HIT{hits.length > 1 ? 'S' : ''}
        </span>
      </div>
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {hits.map((hit, i) => {
          const isOpen = expanded === hit.entity_id;
          const matchColor = hit.match_type === 'EXACT' ? '#FF1F1F' : hit.match_type === 'CLOSE' ? '#FF6B1A' : '#F0B429';
          const props = (hit.properties ?? {}) as Record<string, unknown>;
          const notes = (props.notes as string[]) ?? [];
          const country = (props.country as string[]) ?? [];
          const datasets = hit.datasets;

          return (
            <div key={i} style={{ background: '#0C1118', border: `1px solid ${matchColor}20`, borderLeft: `3px solid ${matchColor}`, borderRadius: 8 }}>
              <button
                onClick={() => setExpanded(isOpen ? null : hit.entity_id)}
                className="w-full text-left"
                style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
              >
                {/* Score gauge */}
                <div style={{ position: 'relative', width: 42, height: 42, flexShrink: 0 }}>
                  <svg width="42" height="42" viewBox="0 0 42 42">
                    <circle cx="21" cy="21" r="17" fill="none" stroke="#0F1928" strokeWidth="5" />
                    <circle
                      cx="21" cy="21" r="17"
                      fill="none" stroke={matchColor} strokeWidth="5"
                      strokeDasharray={`${Math.round(hit.score * 107)} 107`}
                      strokeLinecap="round"
                      transform="rotate(-90 21 21)"
                    />
                    <text x="21" y="25" textAnchor="middle" fill={matchColor} fontSize="9" fontWeight="800" fontFamily="monospace">
                      {Math.round(hit.score * 100)}
                    </text>
                  </svg>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>{hit.caption}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
                    <span style={{ background: `${matchColor}12`, color: matchColor, fontFamily: 'monospace', fontSize: 7, padding: '2px 7px', borderRadius: 99, border: `1px solid ${matchColor}25` }}>
                      {hit.match_type}
                    </span>
                    {country[0] && (
                      <span style={{ background: '#182030', color: '#5A7A9A', fontFamily: 'monospace', fontSize: 7, padding: '2px 7px', borderRadius: 99 }}>
                        {country[0].toUpperCase()}
                      </span>
                    )}
                    {datasets.slice(0, 2).map(d => (
                      <span key={d} style={{ background: '#182030', color: '#3A5A7A', fontFamily: 'monospace', fontSize: 7, padding: '2px 7px', borderRadius: 99 }}>
                        {d.replace('_', ' ')}
                      </span>
                    ))}
                    {datasets.length > 2 && (
                      <span style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 7 }}>+{datasets.length - 2} more</span>
                    )}
                  </div>
                  {notes[0] && (
                    <div style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8, marginTop: 4 }}>{notes[0]}</div>
                  )}
                </div>

                {isOpen ? <ChevronDown size={12} style={{ color: '#3A5A7A', flexShrink: 0 }} /> : <ChevronRight size={12} style={{ color: '#3A5A7A', flexShrink: 0 }} />}
              </button>

              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ borderTop: '1px solid #182030', overflow: 'hidden' }}
                  >
                    <div style={{ padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {Object.entries(props).map(([k, v]) => {
                        if (!v || k === 'topics') return null;
                        const arr = Array.isArray(v) ? v : [v];
                        return (
                          <div key={k}>
                            <div style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 7, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{k.replace(/_/g, ' ')}</div>
                            {arr.slice(0, 2).map((val, vi) => (
                              <div key={vi} style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 9 }}>{String(val)}</div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Identifier Intelligence Panel ─────────────────────────────────────────────

function IdentifierPanel({ variants, discardedSet, onDiscard }: {
  variants: IdentifierVariant[];
  discardedSet: Set<string>;
  onDiscard: (value: string, undo: boolean) => void;
}) {
  const [filter, setFilter] = useState<string>('ALL');
  const [showDiscarded, setShowDiscarded] = useState(false);

  const confirmed = variants.filter(v => !v.source.match(/grok:(associated|possible|rejected)/));
  const types = ['ALL', ...Array.from(new Set(confirmed.map(v => v.type)))];

  const filtered = filter === 'ALL' ? confirmed : confirmed.filter(v => v.type === filter);
  const activeFiltered = showDiscarded ? filtered : filtered.filter(v => !discardedSet.has(v.value));
  const sorted = [...activeFiltered].sort((a, b) => b.confidence - a.confidence);
  const discardedCount = confirmed.filter(v => discardedSet.has(v.value)).length;

  // Type distribution for the mini bar chart
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const v of confirmed) counts[v.type] = (counts[v.type] ?? 0) + 1;
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [confirmed]);

  const maxCount = Math.max(...typeCounts.map(([, c]) => c), 1);

  return (
    <div style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid #182030', background: '#0C1118' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>
            Identifier Intelligence
          </span>
          <span style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8 }}>
            {confirmed.length} confirmed
          </span>
        </div>

        {/* Type distribution bars */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {typeCounts.slice(0, 6).map(([type, count]) => {
            const c = TYPE_COLORS[type] ?? '#5A7A9A';
            const pct = (count / maxCount) * 100;
            return (
              <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: c, fontFamily: 'monospace', fontSize: 7, width: 60, flexShrink: 0 }}>
                  {SEED_LABELS[type as SeedType] ?? type}
                </span>
                <div style={{ flex: 1, background: '#0F1928', borderRadius: 2, height: 4 }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    style={{ height: 4, borderRadius: 2, background: c, boxShadow: `0 0 4px ${c}60` }}
                  />
                </div>
                <span style={{ color: c, fontFamily: 'monospace', fontSize: 8, fontWeight: 700, width: 14, textAlign: 'right', flexShrink: 0 }}>
                  {count}
                </span>
              </div>
            );
          })}
        </div>

        {/* Type filter chips */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 10 }}>
          {types.map(t => {
            const c = t === 'ALL' ? '#5A7A9A' : (TYPE_COLORS[t] ?? '#5A7A9A');
            const active = filter === t;
            return (
              <button
                key={t}
                onClick={() => setFilter(t)}
                style={{
                  background: active ? `${c}20` : 'transparent',
                  border: `1px solid ${active ? c + '50' : '#182030'}`,
                  color: active ? c : '#3A5A7A',
                  fontFamily: 'monospace', fontSize: 7, padding: '2px 7px',
                  borderRadius: 99, cursor: 'pointer',
                  fontWeight: active ? 700 : 400,
                }}
              >
                {t === 'ALL' ? 'ALL' : (SEED_LABELS[t as SeedType] ?? t)}
              </button>
            );
          })}
        </div>
        {/* Discard toggle */}
        {discardedCount > 0 && (
          <button
            onClick={() => setShowDiscarded(v => !v)}
            style={{
              marginTop: 8, background: 'none', border: 'none', cursor: 'pointer',
              color: '#3A5A7A', fontFamily: 'monospace', fontSize: 7, textDecoration: 'underline',
            }}
          >
            {showDiscarded ? 'Hide' : 'Show'} {discardedCount} discarded
          </button>
        )}
      </div>

      {/* Ranked list */}
      <div style={{ maxHeight: 380, overflowY: 'auto', padding: '8px 0' }}>
        {sorted.map((v, i) => {
          const c = TYPE_COLORS[v.type] ?? '#5A7A9A';
          const Icon = TYPE_ICONS[v.type] ?? Hash;
          const tag = grokTag(v.source);
          const isGrokOk = tag === 'ok';
          const isPivotValidated = v.source.includes('breach_pivot(validated)');
          const isBiDir = v.source.startsWith('breach_validated');
          const isDiscarded = discardedSet.has(v.value);
          const isContact = v.value.startsWith('CONTACT:');
          const isAddressPivot = v.source.includes('address_pivot');

          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.02 }}
              className="group"
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '7px 16px', borderBottom: '1px solid #0A1018',
                opacity: isDiscarded ? 0.4 : 1,
                background: isDiscarded ? '#050709' : undefined,
              }}
            >
              <Icon size={10} style={{ color: isDiscarded ? '#3A5A7A' : c, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  color: isDiscarded ? '#3A5A7A' : '#B0C8E0',
                  fontFamily: 'monospace', fontSize: 9, fontWeight: 600, wordBreak: 'break-all',
                  textDecoration: isDiscarded ? 'line-through' : undefined,
                }}>
                  {v.value}
                </div>
                <div style={{ display: 'flex', gap: 4, marginTop: 2, flexWrap: 'wrap' }}>
                  <span style={{ color: '#283A50', fontFamily: 'monospace', fontSize: 7 }}>
                    {v.source.split('(')[0]}
                  </span>
                  {isGrokOk && <span style={{ color: '#00C853', fontFamily: 'monospace', fontSize: 7 }}>✓ grok</span>}
                  {isBiDir && <span style={{ color: '#00C853', fontFamily: 'monospace', fontSize: 7 }}>⇄ bidi</span>}
                  {isPivotValidated && !isBiDir && <span style={{ color: '#A855F7', fontFamily: 'monospace', fontSize: 7 }}>↳ pivot</span>}
                  {isContact && <span style={{ color: '#F0B429', fontFamily: 'monospace', fontSize: 7 }}>possible contact</span>}
                  {isAddressPivot && !isContact && <span style={{ color: '#F0B429', fontFamily: 'monospace', fontSize: 7 }}>addr pivot</span>}
                  {v.pivot_via && <span style={{ color: '#283A50', fontFamily: 'monospace', fontSize: 7 }}>via {v.pivot_via.slice(0, 20)}</span>}
                  {isDiscarded && <span style={{ color: '#FF4444', fontFamily: 'monospace', fontSize: 7 }}>discarded</span>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                {!isDiscarded && (
                  <>
                    <div style={{ width: 36 }}>
                      <ConfidenceBar value={v.confidence} color={c} height={3} />
                    </div>
                    <span style={{ color: c, fontFamily: 'monospace', fontSize: 8, fontWeight: 700, minWidth: 24, textAlign: 'right' }}>
                      {Math.round(v.confidence * 100)}%
                    </span>
                  </>
                )}
                <button
                  onClick={() => onDiscard(v.value, isDiscarded)}
                  title={isDiscarded ? 'Restore' : 'Discard — mark as irrelevant'}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    background: isDiscarded ? '#00C85318' : '#FF444418',
                    border: `1px solid ${isDiscarded ? '#00C85330' : '#FF444430'}`,
                    color: isDiscarded ? '#00C853' : '#FF4444',
                    borderRadius: 4, padding: '2px 4px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center',
                  }}
                >
                  {isDiscarded ? <RotateCcw size={8} /> : <X size={8} />}
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function CDDResults() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: report, isLoading, isError } = useCDDCase(caseId);
  const [showNetwork, setShowNetwork] = useState(false);

  // Discard state — optimistic local set, persisted to backend
  const [localDiscarded, setLocalDiscarded] = useState<Set<string>>(new Set());
  const serverDiscarded = new Set(report?.identifier_cluster?.discarded_variants ?? []);
  const discardedSet = new Set([...serverDiscarded, ...localDiscarded]);

  const handleDiscard = async (value: string, undo: boolean) => {
    // Optimistic update
    setLocalDiscarded(prev => {
      const next = new Set(prev);
      if (undo) next.delete(value); else next.add(value);
      return next;
    });
    try {
      if (caseId) await profilerApi.discardVariant(caseId, value, undo);
    } catch {
      // Revert on failure
      setLocalDiscarded(prev => {
        const next = new Set(prev);
        if (undo) next.add(value); else next.delete(value);
        return next;
      });
    }
  };

  const eddMutation = useMutation({
    mutationFn: () => profilerApi.runEDD({
      seed: report!.seed ?? report!.identifier_cluster?.canonical ?? '',
      seed_type: report!.seed_type,
      client_ref: report!.client_ref,
    }),
    onSuccess: (result) => navigate(`/app/profiler/case/${result.case_id}/edd`),
  });

  const handleInvestigate = (value: string, seedType: string) => {
    navigate(`/app/profiler?seed=${encodeURIComponent(value)}&type=${seedType}`);
  };

  if (isLoading) {
    return (
      <div style={{ background: '#050709', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', border: '2px solid #00C8FF30', borderTop: '2px solid #00C8FF', animation: 'spin 1s linear infinite' }} />
          <p style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 10 }}>Fetching intelligence…</p>
        </div>
      </div>
    );
  }

  if (isError || !report) {
    return (
      <div style={{ background: '#050709', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#090D13', border: '1px solid #FF1F1F30', borderRadius: 12, padding: 32, textAlign: 'center', maxWidth: 320 }}>
          <Shield size={32} style={{ color: '#FF1F1F', margin: '0 auto 12px' }} />
          <p style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Case Not Found</p>
          <p style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 10, marginBottom: 16 }}>
            Case ID {caseId} does not exist or you lack access.
          </p>
          <button
            onClick={() => navigate('/app/profiler')}
            style={{ background: '#00C8FF18', color: '#00C8FF', border: '1px solid #00C8FF44', fontFamily: 'monospace', fontSize: 10, padding: '8px 16px', borderRadius: 8, cursor: 'pointer' }}
          >
            BACK TO PROFILER
          </button>
        </div>
      </div>
    );
  }

  const tier = report.risk_tier;
  const tierColor = TIER_COLOR[tier];
  const score = normScore(report.risk_score?.overall_score ?? report.overall_score ?? 0);
  const riskScore = report.risk_score;

  const variants = report.identifier_cluster?.variants ?? report.pipeline_result?.cluster?.variants ?? [];
  const canonicalName = variants.find(v => v.type === 'NAME' && v.confidence > 0.4)?.value
    ?? report.seed ?? report.identifier_cluster?.canonical ?? '—';

  const ato = report.pipeline_result?.ato as { breach_count?: number; stealer_log_count?: number; breach_sources?: string[]; tier?: string } | undefined;
  const sanctionsHits = report.pipeline_result?.sanctions?.hits ?? [];

  const riskBreakdownData = riskScore ? [
    { name: 'Sanctions',  value: normScore(riskScore.sanctions_score    ?? 0), color: '#FF1F1F' },
    { name: 'PEP / UBO', value: normScore(riskScore.pep_score           ?? 0), color: '#FF6B1A' },
    { name: 'Adverse',   value: normScore(riskScore.adverse_media_score ?? 0), color: '#F0B429' },
    { name: 'ATO',       value: normScore(riskScore.ato_score           ?? 0), color: '#00C8FF' },
    { name: 'Enforce',   value: normScore(riskScore.enforcement_score   ?? 0), color: '#A855F7' },
  ] : [];

  return (
    <div style={{ background: '#050709', minHeight: '100vh', fontFamily: 'monospace' }}>
      {/* ── Top Bar ── */}
      <div style={{ background: '#070B10', borderBottom: '1px solid #0E1825', padding: '10px 32px', position: 'sticky', top: 0, zIndex: 40 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => navigate('/app/profiler')} style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 9, cursor: 'pointer', background: 'none', border: 'none', letterSpacing: '0.1em' }}>
            PROFILER
          </button>
          <ChevronRight size={9} style={{ color: '#1E2E40' }} />
          <span style={{ color: '#2979FF', fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.1em' }}>CDD REPORT</span>
          <ChevronRight size={9} style={{ color: '#1E2E40' }} />
          <span style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 9 }}>{report.case_reference}</span>
          <div style={{ flex: 1 }} />
          <span style={{ background: '#F0B42912', color: '#F0B429', border: '1px solid #F0B42930', fontFamily: 'monospace', fontSize: 7, padding: '2px 9px', borderRadius: 99, letterSpacing: '0.08em' }}>
            TLP:AMBER
          </span>
          <span style={{ color: '#1E2E40', fontFamily: 'monospace', fontSize: 8 }}>
            {fmt(report.completed_at)}
          </span>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ── Entity Hero Card ── */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{
            background: '#090D13',
            border: `1px solid ${tierColor}30`,
            borderRadius: 14,
            overflow: 'hidden',
            boxShadow: tier === 'CRITICAL' ? `0 0 40px ${tierColor}10` : undefined,
          }}
        >
          {/* Top gradient stripe */}
          <div style={{ height: 3, background: `linear-gradient(90deg, ${tierColor}80, ${tierColor}20, transparent)` }} />

          <div style={{ padding: '20px 28px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 20 }}>
              {/* Avatar */}
              <div style={{
                width: 64, height: 64,
                background: `${tierColor}15`,
                border: `2px solid ${tierColor}40`,
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                boxShadow: `0 0 20px ${tierColor}20`,
              }}>
                <span style={{ color: tierColor, fontWeight: 900, fontSize: 20, fontFamily: 'monospace' }}>{initials(canonicalName)}</span>
              </div>

              {/* Name + meta */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                  <h1 style={{ color: '#DCE8F5', fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: '-0.01em' }}>
                    {canonicalName}
                  </h1>
                  <RiskTierBadge tier={tier} pulse={tier === 'CRITICAL'} size="lg" />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                  <span style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 9 }}>
                    {report.seed ?? report.identifier_cluster?.canonical}
                  </span>
                  <span style={{ color: '#1E2E40' }}>·</span>
                  <span style={{ color: '#283A50', fontFamily: 'monospace', fontSize: 9 }}>{report.case_reference}</span>
                  <span style={{ color: '#1E2E40' }}>·</span>
                  <span style={{ color: '#283A50', fontFamily: 'monospace', fontSize: 9 }}>{report.seed_type}</span>
                </div>
                {/* Flag pills */}
                <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                  {report.sanctions_hit && (
                    <span style={{ background: '#FF1F1F18', color: '#FF1F1F', border: '1px solid #FF1F1F35', fontFamily: 'monospace', fontSize: 8, padding: '3px 10px', borderRadius: 99, fontWeight: 700 }}>
                      SANCTIONS HIT
                    </span>
                  )}
                  {report.pep_hit && (
                    <span style={{ background: '#FF6B1A18', color: '#FF6B1A', border: '1px solid #FF6B1A35', fontFamily: 'monospace', fontSize: 8, padding: '3px 10px', borderRadius: 99, fontWeight: 700 }}>
                      PEP EXPOSURE
                    </span>
                  )}
                  {report.identity_verified && (
                    <span style={{ background: '#00C85318', color: '#00C853', border: '1px solid #00C85335', fontFamily: 'monospace', fontSize: 8, padding: '3px 10px', borderRadius: 99, fontWeight: 700 }}>
                      IDENTITY VERIFIED
                    </span>
                  )}
                  {ato?.tier && (
                    <span style={{ background: '#00C8FF18', color: '#00C8FF', border: '1px solid #00C8FF35', fontFamily: 'monospace', fontSize: 8, padding: '3px 10px', borderRadius: 99 }}>
                      ATO: {ato.tier}
                    </span>
                  )}
                  {(report.degraded_modules ?? []).length > 0 && (
                    <span style={{ background: '#F0B42918', color: '#F0B429', border: '1px solid #F0B42935', fontFamily: 'monospace', fontSize: 8, padding: '3px 10px', borderRadius: 99 }}>
                      {report.degraded_modules!.length} DEGRADED
                    </span>
                  )}
                </div>
              </div>

              {/* Risk score block */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ color: tierColor, fontSize: 48, fontWeight: 900, fontFamily: 'monospace', lineHeight: 1, textShadow: `0 0 20px ${tierColor}60` }}>
                  {score}
                </div>
                <div style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.12em', marginTop: 4 }}>OVERALL RISK</div>
                {riskScore?.confidence !== undefined && (
                  <div style={{ color: '#283A50', fontFamily: 'monospace', fontSize: 8, marginTop: 2 }}>
                    conf {Math.round(riskScore.confidence * 100)}%
                  </div>
                )}
              </div>
            </div>

            {/* Stats strip */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, borderTop: '1px solid #0E1825', paddingTop: 16 }}>
              {[
                { label: 'IDENTIFIERS', value: variants.filter(v => !v.source.match(/grok:(associated|possible|rejected)/)).length, color: '#00C8FF', Icon: Fingerprint },
                { label: 'BREACH DATABASES', value: ato?.breach_sources?.length ?? (variants.some(v => v.source.startsWith('breach')) ? '29+' : 0), color: '#FF6B1A', Icon: Database },
                { label: 'CHECKS RUN', value: (report.check_results ?? []).length, color: '#2979FF', Icon: Shield },
                { label: 'CLUSTER CONF', value: `${Math.round((report.identifier_cluster?.cluster_confidence ?? 0) * 100)}%`, color: '#A855F7', Icon: TrendingUp },
                { label: 'SANCTIONS HITS', value: sanctionsHits.length, color: sanctionsHits.length > 0 ? '#FF1F1F' : '#00C853', Icon: Scale },
                { label: 'STEALER LOGS', value: ato?.stealer_log_count ?? 0, color: ato?.stealer_log_count ? '#FF1F1F' : '#00C853', Icon: AlertTriangle },
              ].map(({ label, value, color, Icon }) => (
                <div key={label} style={{ background: '#0C1118', border: '1px solid #0E1825', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Icon size={16} style={{ color, flexShrink: 0, opacity: 0.8 }} />
                  <div>
                    <div style={{ color, fontFamily: 'monospace', fontSize: 18, fontWeight: 900, lineHeight: 1 }}>{value}</div>
                    <div style={{ color: '#283A50', fontFamily: 'monospace', fontSize: 7, marginTop: 3, letterSpacing: '0.08em' }}>{label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ── Analytics Row ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          style={{ display: 'grid', gridTemplateColumns: '200px 1fr 1fr', gap: 16 }}
        >
          {/* Arc Gauge */}
          <div style={{ background: '#090D13', border: `1px solid ${tierColor}20`, borderRadius: 12, padding: '18px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.18em', marginBottom: 4 }}>RISK ASSESSMENT</p>
            <RiskArcGauge score={score} tier={tier} tierColor={tierColor} />
          </div>

          {/* Radar + bars */}
          <div style={{ background: '#090D13', border: `1px solid ${tierColor}15`, borderRadius: 12, padding: '18px 16px' }}>
            <p style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.18em', marginBottom: 12 }}>RISK DIMENSIONS</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <ResponsiveContainer width="100%" height={150}>
                <RadarChart data={[
                  { s: 'Sanctions', v: normScore(riskScore?.sanctions_score    ?? 0) },
                  { s: 'PEP',       v: normScore(riskScore?.pep_score          ?? 0) },
                  { s: 'Adverse',   v: normScore(riskScore?.adverse_media_score ?? 0) },
                  { s: 'Enforce',   v: normScore(riskScore?.enforcement_score  ?? 0) },
                  { s: 'ATO',       v: normScore(riskScore?.ato_score          ?? 0) },
                ]} margin={{ top: 8, right: 16, bottom: 8, left: 16 }}>
                  <PolarGrid stroke="#0E1825" />
                  <PolarAngleAxis dataKey="s" tick={{ fontSize: 8, fontFamily: 'monospace', fill: '#3A5A7A' }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                  <Radar dataKey="v" stroke={tierColor} fill={tierColor} fillOpacity={0.15} strokeWidth={1.5} />
                </RadarChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'center' }}>
                {riskBreakdownData.map(d => (
                  <div key={d.name}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8 }}>{d.name}</span>
                      <span style={{ color: d.color, fontFamily: 'monospace', fontSize: 8, fontWeight: 700 }}>{d.value}</span>
                    </div>
                    <div style={{ background: '#0F1928', borderRadius: 2, height: 5, overflow: 'hidden' }}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${d.value}%` }}
                        transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                        style={{ height: 5, borderRadius: 2, background: d.color, boxShadow: `0 0 6px ${d.color}60` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Module grid */}
          <div style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12, padding: '18px 16px' }}>
            <ModuleStatusGrid results={report.check_results ?? []} />
          </div>
        </motion.div>

        {/* ── Breach Enrichment Trail ── */}
        {variants.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12, padding: '20px 24px' }}
          >
            <BreachEnrichmentTrail
              variants={variants} ato={ato}
              cluster={report.identifier_cluster}
              discardedSet={discardedSet}
              onDiscard={handleDiscard}
            />
          </motion.div>
        )}

        {/* ── Main 2-column body ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16, alignItems: 'start' }}>

          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Investigative leads */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <InvestigativeLeads variants={variants} onInvestigate={handleInvestigate} />
            </motion.div>

            {/* Sanctions */}
            {sanctionsHits.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.22 }}
              >
                <SanctionsPanel hits={sanctionsHits} />
              </motion.div>
            )}

            {/* Intelligence Network (collapsible) */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.24 }}
              style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12, overflow: 'hidden' }}
            >
              <button
                onClick={() => setShowNetwork(v => !v)}
                style={{ width: '100%', padding: '14px 20px', background: '#0C1118', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: showNetwork ? '1px solid #182030' : 'none' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Network size={12} style={{ color: '#2979FF' }} />
                  <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 10, fontWeight: 700 }}>Identity Network Graph</span>
                  <span style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8 }}>{variants.length} nodes</span>
                </div>
                {showNetwork
                  ? <ChevronDown size={12} style={{ color: '#3A5A7A' }} />
                  : <ChevronRight size={12} style={{ color: '#3A5A7A' }} />}
              </button>
              <AnimatePresence>
                {showNetwork && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div style={{ padding: 16 }}>
                      <NetworkGraph report={report} pipeline={report.pipeline_result} />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Action bar */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.28 }}
              style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12, padding: '14px 20px', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}
            >
              <button
                onClick={() => eddMutation.mutate()}
                disabled={eddMutation.isPending}
                style={{ background: '#A855F718', color: '#A855F7', border: '1px solid #A855F744', fontFamily: 'monospace', fontSize: 10, fontWeight: 700, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: eddMutation.isPending ? 0.5 : 1, letterSpacing: '0.06em' }}
              >
                {eddMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <Shield size={12} />}
                ESCALATE TO EDD
              </button>
              <button
                onClick={() => navigate(`/app/profiler/case/${caseId}/dossier`)}
                style={{ background: '#2979FF18', color: '#2979FF', border: '1px solid #2979FF44', fontFamily: 'monospace', fontSize: 10, fontWeight: 700, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, letterSpacing: '0.06em' }}
              >
                <FileSearch size={12} />
                FULL DOSSIER
              </button>
              <ReportDownloadBar caseId={caseId!} caseRef={report.case_reference} userRole={user?.role} />
              <button
                disabled
                style={{ background: '#F0B42910', color: '#F0B429', border: '1px solid #F0B42930', fontFamily: 'monospace', fontSize: 10, fontWeight: 700, padding: '8px 16px', borderRadius: 8, cursor: 'not-allowed', display: 'flex', alignItems: 'center', gap: 6, opacity: 0.4, letterSpacing: '0.06em' }}
              >
                <BookmarkPlus size={12} />
                WATCHLIST
              </button>
              {eddMutation.isError && (
                <span style={{ color: '#FF1F1F', fontFamily: 'monospace', fontSize: 9 }}>
                  {(eddMutation.error as Error).message}
                </span>
              )}
            </motion.div>
          </div>

          {/* Right column — Identifier intelligence */}
          <motion.div
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            style={{ position: 'sticky', top: 56 }}
          >
            <IdentifierPanel variants={variants} discardedSet={discardedSet} onDiscard={handleDiscard} />
          </motion.div>
        </div>
      </div>
    </div>
  );
}
